import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export type RecordingContextValue = {
  page_key: string;
  context_type?: string;
  context_id?: string;
  client?: string;
  title?: string;
  metadata?: Record<string, unknown>;
};

type RecordingContextStore = {
  recordingContext: RecordingContextValue;
  setRecordingContext: (nextContext: RecordingContextValue) => void;
};

const DEFAULT_RECORDING_CONTEXT: RecordingContextValue = {
  page_key: 'unknown',
  title: 'Voice note',
  metadata: {},
};

const RecordingContext = createContext<RecordingContextStore | undefined>(undefined);

type RecordingContextProviderProps = {
  children: ReactNode;
};

export const RecordingContextProvider = ({ children }: RecordingContextProviderProps) => {
  const [recordingContext, setRecordingContextState] =
    useState<RecordingContextValue>(DEFAULT_RECORDING_CONTEXT);

  const setRecordingContext = useCallback((nextContext: RecordingContextValue) => {
    setRecordingContextState({
      ...nextContext,
      title: nextContext.title ?? 'Voice note',
      metadata: nextContext.metadata ?? {},
    });
  }, []);

  const value = useMemo(
    () => ({
      recordingContext,
      setRecordingContext,
    }),
    [recordingContext, setRecordingContext]
  );

  return <RecordingContext.Provider value={value}>{children}</RecordingContext.Provider>;
};

export const useRecordingContext = () => {
  const context = useContext(RecordingContext);
  if (!context) {
    throw new Error('useRecordingContext must be used within RecordingContextProvider');
  }
  return context;
};
